import { useQuery } from '@tanstack/vue-query'
import { computed, type Ref } from 'vue'

import type { GlossIbexaResponse, GlossType } from '@/types/GlossType'
import useRelation from '@/composables/useRelation'
import { useFetchAudioFile } from '@/composables/useFetchAudioFile'

export const useGlossCollectionQuery = (glossIds: Ref<number[]>) => {
  const { getRelatedContentFields } = useRelation()
  const { fetchAudioFile } = useFetchAudioFile()

  const createGlossEntry = async (gloss: GlossIbexaResponse): Promise<GlossType> => ({
    ...gloss,
    audioFile: await fetchAudioFile(gloss.audioFile?.destinationContentId),
    grammaticalClass: {
      name: gloss.grammaticalClass[0]?.keywords['nor-NO'] as string,
      // The color is stored in the remote_id field of the grammaticalClass tag like this "color{validColorValue}"
      color: gloss.grammaticalClass[0]?.remote_id.split('color')[1] as string,
    }
  })

  const getEntries = async (): Promise<GlossType[]> => {
    const glossCollection = await getRelatedContentFields<GlossIbexaResponse>(glossIds.value)
    return Promise.all(glossCollection.map(createGlossEntry))
  }

  return useQuery<GlossType[]>({
    queryKey: ['glossCollection', glossIds],
    queryFn: getEntries,
    staleTime: Infinity,
    enabled: computed(() => glossIds.value.length > 0),
  })
}
